import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, KeyRound, ArrowRight, ArrowLeft, Car, DollarSign, Zap, Wrench,
  ShieldCheck, Rocket, TrendingUp, TrendingDown, Calendar, Layers, Cpu,
  CreditCard, CheckCircle2, ChevronDown, Download, AlertTriangle, Repeat,
  Recycle, Target,
} from "lucide-react";
import { exportPitchPptx, flattenSlideForPptx } from "@/lib/exportPitchPptx";

const PIN = "1215";
const STORAGE_KEY = "advantage_pitch_unlocked";

// ============ CONTENT MODEL ============
const SLIDES = [
  {
    id: 1,
    kicker: "// 01 · COVER & VISION",
    title: "Advantage Fleet — the <span class=\"text-emerald-400\">high-yield, debt-free</span> gig-worker mobility engine.",
    body:
      "Disrupting gig-worker vehicle rentals via distressed-asset sourcing and cost-controlled in-house refurbishing. Providing affordable, reliable $150–$200/week rental vehicles for rideshare and delivery workers while generating 100%+ cash-on-cash annual yields with zero vehicle debt.",
    bullets: [
      "$150–$200 / week driver pricing — half the cost of Hertz / Avis / HyreCar",
      "Sub-$2,000 distressed acquisition + $1,500 standardized drivetrain swap = $3,165 all-in per unit",
      "208.5% Year-1 cash-on-cash ROI · 5.7-month payback per vehicle",
      "Zero vehicle debt · 100% capital recycling on 12–18 month resale cycles",
    ],
    accent: "emerald",
  },
  {
    id: 2,
    kicker: "// 02 · THE PROBLEM",
    title: "A three-sided market inefficiency — <span class=\"text-emerald-400\">hiding in plain sight</span>.",
    body:
      "Gig workers, traditional fleet operators, and the used-car market are each stuck in their own trap. Advantage sits at the intersection of all three.",
    problems: [
      {
        tag: "GIG WORKERS",
        title: "High barrier to entry",
        desc: "Uber / Lyft / DoorDash / Amazon Flex drivers face $280–$380/week commercial rental rates through Hertz, Avis, and HyreCar — plus strict credit hurdles that gate 40%+ of the willing labor pool out entirely.",
        icon: AlertTriangle,
        tone: "red",
      },
      {
        tag: "TRADITIONAL OPERATORS",
        title: "Crushed by fleet overhead",
        desc: "Standard rental operators buy expensive financed assets ($25,000+ per vehicle), resulting in unsustainable debt service — $700+ per car per month — before insurance, maintenance, or utilization risk enters the equation.",
        icon: TrendingDown,
        tone: "red",
      },
      {
        tag: "USED-CAR MARKET",
        title: "Distressed-asset firehose",
        desc: "Thousands of mechanically distressed vehicles (bad transmissions, blown head gaskets, blown motors) are listed daily on Facebook Marketplace and dealer auctions for scrap value — $800 to $2,000 — with almost no institutional buyers.",
        icon: Recycle,
        tone: "amber",
      },
    ],
    accent: "emerald",
  },
  {
    id: 3,
    kicker: "// 03 · THE ADVANTAGE SOLUTION",
    title: "Buy distressed. <span class=\"text-emerald-400\">Repair predictable.</span> Rent weekly. Recycle capital.",
    body:
      "A vertically integrated engine that converts $1,200 marketplace scrap into $700/month recurring rental revenue — with capped, in-house labor costs and a Stripe-automated driver subscription front end.",
    solution: [
      {
        title: "Low-Cost Sourcing Engine",
        desc: "Direct acquisition of private-party mechanics-specials and distressed fleet assets via Facebook Marketplace and dealer wholesale auctions — under $2,000 per unit.",
        icon: Target,
      },
      {
        title: "Standardized Refurbishing Matrix",
        desc: "Dedicated in-house mechanical turnaround with fixed, standardized labor rates. Drivetrains, engines, and heads all quoted from a published matrix — zero cost drift.",
        icon: Wrench,
      },
      {
        title: "Accessible Weekly Driver Pricing",
        desc: "Turnkey, insured vehicles at $150–$200 per week ($600–$800/month) with low deposits, automated weekly ACH / card billing, and built-in maintenance.",
        icon: CreditCard,
      },
    ],
    accent: "emerald",
  },
  {
    id: 4,
    kicker: "// 04 · IN-HOUSE LABOR & REFURBISHING MATRIX",
    title: "Predictable, capped labor costs — the <span class=\"text-emerald-400\">core moat</span>.",
    body:
      "Every refurbishing operation is quoted from a published labor matrix. No surprises, no cost overruns, no shop mark-ups. This is what turns a $1,200 distressed marketplace car into a reliable fleet asset.",
    matrix: [
      { service: "Mobile Diagnostic & Pre-Buy Scan",       labor: "$65",    parts: "$0",             total: "$65" },
      { service: "4-Cylinder Engine Swap (Pull & Replace)", labor: "$800",   parts: "$500 – $800",     total: "$1,300 – $1,600" },
      { service: "Standard Engine Swap (Pull & Replace)",   labor: "$1,000", parts: "$600 – $900",     total: "$1,600 – $1,900" },
      { service: "Foreign Engine Swap (Pull & Replace)",    labor: "$1,500", parts: "$800 – $1,200",   total: "$2,300 – $2,700" },
      { service: "Transmission Swap (Pull & Replace)",      labor: "$800",   parts: "$450 – $750",     total: "$1,250 – $1,550" },
      { service: "4-Cylinder Head Replacement",             labor: "$800",   parts: "$250 – $350",     total: "$1,050 – $1,150" },
      { service: "V6 Head Replacement",                     labor: "$1,100", parts: "$400 – $550",     total: "$1,500 – $1,650" },
    ],
    accent: "emerald",
  },
  {
    id: 5,
    kicker: "// 05 · UNIT ECONOMICS · FB MARKETPLACE CASE STUDY",
    title: "$3,165 in → <span class=\"text-emerald-400\">$6,600 / year out</span>. 5.7-month payback.",
    body:
      "A single vehicle walkthrough from marketplace listing to first-year net cash flow. Standard operating case — not best-case.",
    walkthrough: {
      flow: [
        { step: "01", label: "Sourcing", value: "$1,200", detail: "Distressed FB Marketplace private-party purchase" },
        { step: "02", label: "Refurbish", value: "$1,500", detail: "Transmission or engine swap · labor + LKQ parts" },
        { step: "03", label: "Cash Flow", value: "$700 / mo", detail: "Gig-worker rental @ $175 / week" },
      ],
      costs: [
        ["Acquisition (Private FB Seller)", "$1,200.00"],
        ["Pre-Buy Diagnostic", "$65.00"],
        ["Major Drivetrain Swap (Labor + LKQ)", "$1,500.00"],
        ["Wear & Tear Prep (Fluids · Brakes · Detail · GPS)", "$400.00"],
        ["Total All-In Acquisition CapEx", "$3,165.00"],
      ],
      income: [
        ["Gross Rental Income · $175 × 48 wks", "$8,400.00 / yr"],
        ["Commercial Insurance + Maintenance Reserve", "-$1,800.00 / yr"],
        ["Net Annual Cash Flow", "$6,600.00 / yr"],
      ],
      kpis: [
        { label: "Payback period", value: "5.7 months", tone: "good" },
        { label: "Year-1 cash-on-cash ROI", value: "208.5%", tone: "good" },
        { label: "Break-even week", value: "~week 23", tone: "good" },
        { label: "Debt service per unit", value: "$0", tone: "good" },
      ],
    },
    accent: "emerald",
  },
  {
    id: 6,
    kicker: "// 06 · GIG-WORKER SUBSCRIPTION MODEL",
    title: "$150–$200 / week driver subscription — <span class=\"text-emerald-400\">frictionless & enforceable</span>.",
    body:
      "The consumer-side of Advantage runs like a modern SaaS subscription — automated billing, low deposit, maintenance-included, and hardware-enforced payment compliance.",
    features: [
      { title: "Weekly Autopay (Stripe)",   desc: "Automated recurring charges every Monday morning prior to key release.", icon: Repeat },
      { title: "Accessible Deposit",        desc: "$200–$250 refundable hold vs. $500+ commercial alternatives — expands the eligible driver pool.", icon: CreditCard },
      { title: "Maintenance Guarantee",     desc: "Routine oil changes, tire rotations, and brake wear covered by Advantage — minimizes driver downtime.", icon: Wrench },
      { title: "Telematics & Loss Prevention", desc: "Hardwired 4G OBD-II GPS with automated starter kill switches tied to weekly payment status.", icon: ShieldCheck },
      { title: "Automated Onboarding",      desc: "Pre-qualified gig drivers self-serve through MyAdvantageCarRental.com — license upload, deposit, agreement in one flow.", icon: Rocket },
      { title: "Multi-Platform Approved",   desc: "Every vehicle prepped and papered for Uber / Lyft / DoorDash / Amazon Flex acceptance out of the gate.", icon: Car },
    ],
    accent: "emerald",
  },
  {
    id: 7,
    kicker: "// 07 · FLEET EXIT & CAPITAL RECYCLING",
    title: "Every unit becomes <span class=\"text-emerald-400\">two units</span> — indefinitely.",
    body:
      "12–18 months of rental income is not the end of the vehicle's usefulness. The exit strategy turns each unit into a compound-growth machine — no debt, no dilution.",
    recycling: [
      { phase: "MONTH 0–17",  title: "Active Rental Lifespan", desc: "Vehicle stays in the fleet for 12–18 months. During that window it has already paid itself back 2–3× at $175/wk.", tone: "emerald" },
      { phase: "MONTH 18",    title: "Resale Liquidation",     desc: "Detail + list on Facebook Marketplace, Craigslist, or a local dealer lot at $3,000–$4,000. Newly installed drivetrain is the marketing hook.", tone: "cyan" },
      { phase: "MONTH 18+",   title: "Capital Velocity",       desc: "Resale recovers 100%+ of original CapEx. That capital immediately funds 2 additional distressed marketplace units — no bank required.", tone: "emerald" },
    ],
    accent: "emerald",
  },
  {
    id: 8,
    kicker: "// 08 · 10-VEHICLE PILOT EXPANSION",
    title: "$32k in → <span class=\"text-emerald-400\">$101k value created</span> in 12 months.",
    body:
      "The pilot fleet math — deploying 10 vehicles for a single year proves the model at capital scale and produces $101,000 in year-1 value against a $32,000 deployment.",
    pilot: {
      inflows: [
        ["Total Deployment Capital (10 units × ~$3,200 all-in)",   "$32,000"],
        ["Gross Annual Rental Revenue (10 × $175/wk × 48)",         "$84,000"],
      ],
      outflows: [
        ["Blanket Commercial Insurance + Maintenance",              "-$18,000"],
      ],
      net: [
        ["Net Annual Operating Profit",                             "$66,000 / yr"],
        ["End-of-Cycle Capital Liquidation (10 × $3,500)",          "$35,000"],
        ["Total 1-Year Pilot Value Creation",                       "$101,000"],
      ],
      kpis: [
        { label: "Deployment capital",       value: "$32,000",  tone: "neutral" },
        { label: "Net operating profit",     value: "$66,000",  tone: "good" },
        { label: "Liquidation recovery",     value: "$35,000",  tone: "good" },
        { label: "Total value creation",     value: "$101,000", tone: "good" },
      ],
    },
    accent: "emerald",
  },
  {
    id: 9,
    kicker: "// 09 · COMPETITIVE MOAT",
    title: "Three structural advantages, <span class=\"text-emerald-400\">stacked</span>.",
    body:
      "Each of these advantages is defensible on its own. Stacked, they compound into a moat that traditional car-rental operators can't cross without rebuilding their entire cost structure.",
    moat: [
      {
        title: "Zero Debt Overhead",
        desc: "No bank notes · $0 monthly financing liability creates resilience against utilization dips. When a competitor loses a driver, they lose money — when Advantage loses one, it costs nothing.",
        icon: TrendingDown,
      },
      {
        title: "Standardized In-House Repair Cost",
        desc: "Knowing exact labor caps ($800 trans, $800–$1,500 engines) turns distressed $1,000 marketplace cars into reliable fleet revenue. Nobody else in this niche has this pricing discipline.",
        icon: Wrench,
      },
      {
        title: "High-Demand, Sticky Niche",
        desc: "Gig drivers provide long-term, multi-month continuous rentals — drastically reducing turnaround churn and cleaning costs vs. daily rental competitors. Retention = margin.",
        icon: Target,
      },
    ],
    accent: "emerald",
  },
  {
    id: 10,
    kicker: "// 10 · CALL TO ACTION",
    title: "Three phases. <span class=\"text-emerald-400\">One launch.</span>",
    body:
      "The path from investor commitment to first driver on the road — mapped in three executable phases.",
    phases: [
      {
        week: "PHASE 1",
        title: "Secure Inventory",
        items: [
          "Identify 5 initial mechanics-special targets on Facebook Marketplace",
          "Execute pre-buy diagnostics ($65/unit)",
          "Close acquisitions at or under $2,000 per unit",
        ],
      },
      {
        week: "PHASE 2",
        title: "Refurbish & Instrument",
        items: [
          "Standardized drivetrain replacements via the labor matrix",
          "Hardwire 4G OBD-II GPS + starter kill switches on all units",
          "Detail, insure, and title each vehicle for gig-platform acceptance",
        ],
      },
      {
        week: "PHASE 3",
        title: "Onboard Drivers",
        items: [
          "Open MyAdvantageCarRental.com to pre-qualified gig drivers",
          "Automated Stripe weekly billing goes live",
          "Ramp to 5 active drivers within 30 days · 10 within 60",
        ],
      },
    ],
    accent: "emerald",
  },
];

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) document.getElementById(`adv-pin-${i + 1}`)?.focus();
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) document.getElementById(`adv-pin-${i - 1}`)?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="advantage-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/12 blur-[140px]" />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
            <Lock size={26} className="text-emerald-400" />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-3">// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">Advantage Fleet · Pitch Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            This deck contains confidential financial and operational information. Enter your 4-digit access PIN to unlock.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`adv-pin-${i}`}
              data-testid={`advantage-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "border-emerald-400 shadow-[0_0_15px_-3px_rgba(16,185,129,0.5)]" : "border-slate-800 focus:border-emerald-500/60"}`}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ EXECUTIVE SUMMARY ============
function ExecutiveSummary() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="advantage-summary">
      <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/12 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20">
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]">
          <ShieldCheck size={12} /> UNLOCKED · Advantage Fleet Solutions · Confidential
        </motion.div>

        <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98]">
          Advantage Fleet · <span className="text-emerald-400">Gig-Worker Mobility Engine</span>.
        </motion.h1>

        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-6 text-base sm:text-lg text-slate-300 max-w-4xl leading-relaxed">
          A vertically integrated rental fleet targeting rideshare and delivery gig workers. Distressed marketplace acquisition (&lt;$2k), in-house standardized drivetrain refurbishing (~$1.5k), and Stripe-automated weekly driver subscription ($150–$200/wk) yield <span className="text-emerald-400 font-semibold">208.5% Year-1 cash-on-cash ROI</span> at a <span className="text-emerald-400 font-semibold">5.7-month payback per unit</span> — with <span className="text-emerald-400 font-semibold">zero vehicle debt</span> and full capital recycling on 12–18 month resale cycles.
        </motion.p>

        {/* TL;DR grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "// THE STRATEGY", t: "Distressed → Refurbished → Rented", d: "Marketplace mechanics-specials become gig-driver-ready assets via a published, capped-labor refurbishing matrix.", accent: "emerald" },
            { k: "// THE UNIT ECON", t: "$3,165 in · $700/mo out", d: "Standard walkthrough per unit. Break-even week 23. Zero debt overhead. 48-week active rental window.", accent: "emerald" },
            { k: "// THE PILOT",    t: "10 units · $101k value / yr", d: "$32k capital deployment produces $66k net operating profit + $35k liquidation recovery = $101k total year-1.", accent: "cyan" },
            { k: "// THE MOAT",     t: "Zero debt · capped labor · sticky driver", d: "Three structural cost advantages stacked. Traditional rental operators cannot cross this without demolishing their financing model.", accent: "emerald" },
          ].map((c, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.3 + i*0.05}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${c.accent === "cyan" ? "text-cyan-400" : "text-emerald-400"}`}>{c.k}</div>
              <h3 className="mt-2 font-display text-lg font-bold">{c.t}</h3>
              <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#deck" data-testid="advantage-cta-view-deck" className="inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-400 hover:scale-[1.02] transition-all shadow-[0_0_30px_-8px_rgba(16,185,129,0.6)]">
            <ArrowRight size={13} /> View Full Deck
          </a>
          <a href="#deck" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }} className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 transition-all">
            <ChevronDown size={13} /> Scroll Through
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ INFOGRAPHIC ============
function Infographic() {
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80" data-testid="advantage-infographic">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">// AT-A-GLANCE INFOGRAPHIC</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The whole engine, <span className="text-emerald-400">one screen</span>.</h2>
        </motion.div>

        {/* KPI row */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "$3,165", v: "all-in per vehicle", Icon: DollarSign, color: "text-slate-100" },
            { k: "5.7 mo",  v: "payback per unit",   Icon: TrendingUp, color: "text-emerald-400" },
            { k: "208.5%",  v: "year-1 cash-on-cash ROI", Icon: Rocket, color: "text-emerald-400" },
            { k: "$0",      v: "vehicle debt service", Icon: ShieldCheck, color: "text-cyan-400" },
          ].map((s, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.06}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <s.Icon size={16} className={s.color} />
              <div className={`mt-3 font-display text-4xl lg:text-5xl font-bold tracking-tighter ${s.color}`}>{s.k}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.v}</div>
            </motion.div>
          ))}
        </div>

        {/* Flow visual + cost comparison */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          {/* Cost comparison bar */}
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">// DRIVER PRICING GAP</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">We cut driver cost by <span className="text-emerald-400">~48%</span></h3>

            <div className="mt-6 space-y-4">
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-red-400 flex items-center gap-1.5"><TrendingUp size={11} /> Hertz / Avis / HyreCar</span>
                  <span className="text-slate-100 font-bold">$280 – $380 / wk</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"100%"}} viewport={{once:true}} transition={{duration:1.2,ease:"easeOut"}} className="h-full bg-gradient-to-r from-red-600 to-red-400" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-emerald-400 flex items-center gap-1.5"><TrendingDown size={11} /> Advantage Fleet</span>
                  <span className="text-slate-100 font-bold">$150 – $200 / wk</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"53%"}} viewport={{once:true}} transition={{duration:1.4,delay:0.3,ease:"easeOut"}} className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400" />
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-emerald-500/30 bg-emerald-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Weekly savings / driver</div>
                <div className="mt-1 font-display text-2xl font-bold text-emerald-400">$130 – $180</div>
              </div>
              <div className="border border-emerald-500/30 bg-emerald-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Annual savings / driver</div>
                <div className="mt-1 font-display text-2xl font-bold text-emerald-400">$6.7k – $9.4k</div>
              </div>
            </div>
          </motion.div>

          {/* 3-step flow */}
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">// THE 3-STEP FLOW</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Marketplace → Refurbish → <span className="text-emerald-400">Recurring Revenue</span>.</h3>

            <div className="mt-6 space-y-3">
              {[
                { n: "01", t: "SOURCE",    d: "FB Marketplace distressed purchase", v: "$1,200",       tone: "amber" },
                { n: "02", t: "REFURBISH", d: "Standardized trans / engine + prep",  v: "$1,965",       tone: "cyan" },
                { n: "03", t: "RENT",      d: "Gig worker · $175 / wk · 48 wks",     v: "$8,400 / yr",  tone: "emerald" },
              ].map((s) => {
                const styles = {
                  amber:   { box: "border-amber-500/40 bg-amber-500/[0.04]",     chip: "bg-amber-500/10 border-amber-500/50 text-amber-400",     label: "text-amber-400",   value: "text-amber-400" },
                  cyan:    { box: "border-cyan-500/40 bg-cyan-500/[0.04]",       chip: "bg-cyan-500/10 border-cyan-500/50 text-cyan-400",       label: "text-cyan-400",    value: "text-cyan-400" },
                  emerald: { box: "border-emerald-500/40 bg-emerald-500/[0.04]", chip: "bg-emerald-500/10 border-emerald-500/50 text-emerald-400", label: "text-emerald-400", value: "text-emerald-400" },
                }[s.tone];
                return (
                  <div key={s.n} className={`flex items-center gap-4 border rounded-xl p-3.5 ${styles.box}`}>
                    <div className={`h-10 w-10 rounded-lg border flex items-center justify-center font-mono text-[11px] font-bold shrink-0 ${styles.chip}`}>{s.n}</div>
                    <div className="flex-1">
                      <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${styles.label}`}>{s.t}</div>
                      <div className="mt-0.5 text-[13px] text-slate-200">{s.d}</div>
                    </div>
                    <div className={`font-display text-lg font-bold ${styles.value}`}>{s.v}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* 10-car pilot strip */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="mt-4 rounded-2xl p-6 bg-gradient-to-br from-emerald-500/10 via-slate-900/60 to-cyan-500/10 border border-emerald-500/40 backdrop-blur-xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">// 10-CAR PILOT · YR 1</div>
              <h3 className="mt-2 font-display text-2xl lg:text-3xl font-bold tracking-tight">$32k in → <span className="text-emerald-400">$101k out</span> in 12 months.</h3>
              <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">$66k net operating profit + $35k liquidation recovery on 10-car deployment. Every dollar recovered redeploys into 2 new distressed marketplace units without debt.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3"><div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Deploy</div><div className="mt-1 font-display text-xl font-bold text-slate-100">$32,000</div></div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3"><div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Net profit / yr</div><div className="mt-1 font-display text-xl font-bold text-emerald-400">$66,000</div></div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3"><div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Liquidation</div><div className="mt-1 font-display text-xl font-bold text-emerald-400">$35,000</div></div>
              <div className="border border-emerald-500/40 bg-slate-950/40 rounded-xl p-3"><div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Total value</div><div className="mt-1 font-display text-xl font-bold text-emerald-400">$101,000</div></div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ SLIDE RENDERERS ============
function SlideBody({ slide }) {
  const heading = (
    <>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400 mb-4">{slide.kicker}</div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02]" dangerouslySetInnerHTML={{ __html: slide.title }} />
      <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl">{slide.body}</p>
    </>
  );

  // Bullets (slides 1, 9)
  if (slide.bullets) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {slide.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-slate-900/40 border border-slate-800/60 rounded-2xl p-4">
              <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-200 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Problems (slide 2)
  if (slide.problems) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.problems.map((p, i) => {
            const Icon = p.icon;
            const isRed = p.tone === "red";
            const box = isRed
              ? "border-red-500/40 bg-red-500/[0.04]"
              : "border-amber-500/40 bg-amber-500/[0.04]";
            const iconWrap = isRed
              ? "bg-red-500/10 border-red-500/50"
              : "bg-amber-500/10 border-amber-500/50";
            const iconColor = isRed ? "text-red-400" : "text-amber-400";
            return (
              <div key={i} className={`rounded-2xl p-5 border-2 ${box}`}>
                <div className={`h-9 w-9 rounded-xl border flex items-center justify-center ${iconWrap}`}>
                  <Icon size={15} className={iconColor} />
                </div>
                <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.22em] ${iconColor}`}>{p.tag}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{p.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Solution (slide 3)
  if (slide.solution) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.solution.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border border-emerald-500/40 bg-slate-900/50">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center"><Icon size={16} className="text-emerald-400" /></div>
                <h4 className="mt-3 font-display text-lg font-bold text-slate-100">{s.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Matrix table (slide 4)
  if (slide.matrix) {
    return (
      <div>
        {heading}
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/50">
          <div className="hidden md:grid md:grid-cols-[2fr_1fr_1.4fr_1.6fr] gap-4 px-5 py-3 border-b border-slate-800/80 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 bg-slate-950/40">
            <div>Operational Service</div>
            <div>Labor Rate</div>
            <div>Parts (LKQ / Wholesale)</div>
            <div>Total Repair Target</div>
          </div>
          {slide.matrix.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.4fr_1.6fr] gap-4 px-5 py-3.5 border-b border-slate-800/50 last:border-b-0 text-[13px]">
              <div className="text-slate-200">{r.service}</div>
              <div className="font-mono text-slate-300"><span className="md:hidden font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mr-2">Labor</span>{r.labor}</div>
              <div className="font-mono text-slate-300"><span className="md:hidden font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mr-2">Parts</span>{r.parts}</div>
              <div className="font-mono font-bold text-emerald-400"><span className="md:hidden font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mr-2">Total</span>{r.total}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3 border border-emerald-500/30 bg-emerald-500/[0.04] rounded-xl p-4">
          <ShieldCheck size={18} className="text-emerald-400 shrink-0" />
          <div className="text-[13px] text-slate-200 leading-relaxed">
            <span className="text-emerald-400 font-semibold">Cost predictability = moat.</span> Every distressed unit gets a diagnosed, quoted, capped repair before purchase — turning speculative marketplace buys into disciplined fleet acquisitions.
          </div>
        </div>
      </div>
    );
  }

  // Walkthrough (slide 5)
  if (slide.walkthrough) {
    const w = slide.walkthrough;
    return (
      <div>
        {heading}
        {/* 3-step flow */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {w.flow.map((s, i) => (
            <div key={i} className="relative rounded-2xl p-5 border border-emerald-500/40 bg-slate-900/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">STEP {s.step} · {s.label}</div>
              <div className="mt-3 font-display text-3xl font-bold text-emerald-400">{s.value}</div>
              <div className="mt-2 text-[12px] text-slate-400 leading-relaxed">{s.detail}</div>
              {i < w.flow.length - 1 && <ArrowRight size={18} className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 text-emerald-400" />}
            </div>
          ))}
        </div>

        {/* Cost + Income tables */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-5 border border-slate-800/80 bg-slate-900/50">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3">CAPEX per unit</div>
            <div className="divide-y divide-slate-800/60">
              {w.costs.map(([k, v], i) => {
                const isTotal = i === w.costs.length - 1;
                return (
                  <div key={i} className={`flex items-center justify-between py-2.5 ${isTotal ? "pt-3.5 border-t-2 border-slate-700/70" : ""} text-[13px]`}>
                    <span className={isTotal ? "text-slate-100 font-semibold" : "text-slate-400"}>{k}</span>
                    <span className={`font-mono font-bold ${isTotal ? "text-emerald-400" : "text-slate-200"}`}>{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl p-5 border-2 border-emerald-500/50 bg-slate-900/50 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 mb-3">Year-1 income per unit</div>
            <div className="divide-y divide-slate-800/60">
              {w.income.map(([k, v], i) => {
                const isTotal = i === w.income.length - 1;
                return (
                  <div key={i} className={`flex items-center justify-between py-2.5 ${isTotal ? "pt-3.5 border-t-2 border-emerald-500/50" : ""} text-[13px]`}>
                    <span className={isTotal ? "text-slate-100 font-semibold" : "text-slate-400"}>{k}</span>
                    <span className={`font-mono font-bold ${isTotal ? "text-emerald-400 text-lg" : "text-slate-200"}`}>{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {w.kpis.map((k, i) => (
            <div key={i} className="border border-emerald-500/40 bg-emerald-500/[0.05] rounded-xl p-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">{k.label}</div>
              <div className="mt-1.5 font-display text-2xl font-bold text-emerald-400">{k.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Features (slide 6)
  if (slide.features) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-5">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center"><Icon size={15} className="text-emerald-400" /></div>
                <h4 className="mt-3 font-display text-base font-bold text-slate-100">{f.title}</h4>
                <p className="mt-1.5 text-[12px] text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Recycling (slide 7)
  if (slide.recycling) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.recycling.map((r, i) => {
            const isCyan = r.tone === "cyan";
            const box = isCyan
              ? "border-cyan-500/40 bg-cyan-500/[0.04]"
              : "border-emerald-500/40 bg-emerald-500/[0.04]";
            const iconWrap = isCyan
              ? "bg-cyan-500/10 border-cyan-500/50"
              : "bg-emerald-500/10 border-emerald-500/50";
            const iconColor = isCyan ? "text-cyan-400" : "text-emerald-400";
            return (
              <div key={i} className={`rounded-2xl p-5 border ${box}`}>
                <div className={`h-10 w-10 rounded-xl border flex items-center justify-center ${iconWrap}`}>
                  <Repeat size={16} className={iconColor} />
                </div>
                <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.22em] ${iconColor}`}>{r.phase}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{r.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{r.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 border border-emerald-500/30 bg-emerald-500/[0.04] rounded-2xl p-5 flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center shrink-0"><Recycle size={18} className="text-emerald-400" /></div>
          <div className="text-sm text-slate-200 leading-relaxed">
            <span className="text-emerald-400 font-semibold">Compounding loop:</span> resale recovers 100%+ of the original CapEx, which funds 2 new distressed marketplace units. Fleet doubles roughly every 18 months without debt — indefinitely.
          </div>
        </div>
      </div>
    );
  }

  // Pilot (slide 8)
  if (slide.pilot) {
    const p = slide.pilot;
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-5 border border-slate-800/80 bg-slate-900/50">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3">Inflows / Outflows</div>
            <div className="divide-y divide-slate-800/60">
              {[...p.inflows, ...p.outflows].map(([k, v], i) => (
                <div key={i} className="flex items-center justify-between py-2.5 text-[13px]">
                  <span className="text-slate-400">{k}</span>
                  <span className={`font-mono font-bold ${v.startsWith("-") ? "text-red-400" : "text-slate-200"}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl p-5 border-2 border-emerald-500/50 bg-slate-900/50 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 mb-3">Value creation</div>
            <div className="divide-y divide-slate-800/60">
              {p.net.map(([k, v], i) => {
                const isTotal = i === p.net.length - 1;
                return (
                  <div key={i} className={`flex items-center justify-between py-2.5 ${isTotal ? "pt-3.5 border-t-2 border-emerald-500/50" : ""} text-[13px]`}>
                    <span className={isTotal ? "text-slate-100 font-semibold" : "text-slate-300"}>{k}</span>
                    <span className={`font-mono font-bold ${isTotal ? "text-emerald-400 text-lg" : "text-slate-200"}`}>{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {p.kpis.map((k, i) => (
            <div key={i} className={`border rounded-xl p-4 ${k.tone === "good" ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-slate-800/60 bg-slate-900/50"}`}>
              <div className={`font-mono text-[9px] uppercase tracking-[0.22em] ${k.tone === "good" ? "text-emerald-400" : "text-slate-400"}`}>{k.label}</div>
              <div className={`mt-1.5 font-display text-2xl font-bold ${k.tone === "good" ? "text-emerald-400" : "text-slate-100"}`}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Moat (slide 9)
  if (slide.moat) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.moat.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border-2 border-emerald-500/50 bg-slate-900/50">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/50 flex items-center justify-center"><Icon size={16} className="text-emerald-400" /></div>
                <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-400">MOAT LAYER {String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{m.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{m.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Phases (slide 10)
  if (slide.phases) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-3 gap-3">
          {slide.phases.map((p, i) => (
            <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center"><Calendar size={15} className="text-emerald-400" /></div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">{p.week}</div>
                  <div className="font-display text-lg font-bold">{p.title}</div>
                </div>
              </div>
              <ul className="space-y-2">
                {p.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-2 text-[13px] text-slate-300 leading-relaxed">
                    <ArrowRight size={12} className="text-emerald-400 mt-1 shrink-0" /><span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// ============ DECK ============
function Deck() {
  const [i, setI] = useState(0);
  const [printing, setPrinting] = useState(false);
  const next = useCallback(() => setI((v) => Math.min(SLIDES.length - 1, v + 1)), []);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);
  const slide = SLIDES[i];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    const beforePrint = () => setPrinting(true);
    const afterPrint = () => setPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [next, prev]);

  return (
    <section id="deck" className="relative py-20 lg:py-28 scroll-mt-24" data-testid="advantage-deck">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">// FULL PITCH DECK · {SLIDES.length} SLIDES</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The deck.</h2>
          <p className="mt-3 text-slate-400 text-sm">Use ← → arrow keys or click the pager below to navigate. Slide {i + 1} of {SLIDES.length}.</p>
        </motion.div>

        {printing ? (
          <div className="mt-8 space-y-6">
            {SLIDES.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:p-12" data-testid={`advantage-slide-${s.id}`}>
                <SlideBody slide={s} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mt-8 relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-6 lg:p-12 min-h-[560px] overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-emerald-500/10 blur-[120px]" aria-hidden />
              <AnimatePresence mode="wait">
                <motion.div key={slide.id} initial={{opacity:0, x:30}} animate={{opacity:1, x:0}} exit={{opacity:0, x:-30}} transition={{duration:0.35, ease:[0.22,1,0.36,1]}} className="relative" data-testid={`advantage-slide-${slide.id}`}>
                  <SlideBody slide={slide} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pager */}
            <div className="mt-6 flex items-center justify-between pitch-no-print">
              <button onClick={prev} disabled={i === 0} data-testid="advantage-deck-prev" className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ArrowLeft size={13} /> Prev
              </button>
              <div className="flex items-center gap-1.5">
                {SLIDES.map((_, idx) => (
                  <button key={idx} onClick={() => setI(idx)} data-testid={`advantage-deck-dot-${idx}`} aria-label={`Go to slide ${idx + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-emerald-400" : "w-2 bg-slate-700 hover:bg-slate-500"}`} />
                ))}
              </div>
              <button onClick={next} disabled={i === SLIDES.length - 1} data-testid="advantage-deck-next" className="inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_-6px_rgba(16,185,129,0.6)]">
                Next <ArrowRight size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function AdvantagePitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  const title = useMemo(() => "Advantage Fleet · Pitch Vault", []);
  useEffect(() => {
    document.title = title;
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, [title]);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="advantage-pitch-page">
      <ExecutiveSummary />
      <Infographic />
      <Deck />

      {/* Footer strip */}
      <section className="border-t border-slate-800/80 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · Advantage Fleet Solutions · Prepared by TraceElements
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="advantage-download-pdf" className="inline-flex items-center gap-2 bg-emerald-500 text-slate-950 hover:bg-emerald-400 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(16,185,129,0.6)]">
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({
                vaultName: "Advantage Fleet",
                title: "Advantage Fleet · Gig-Worker Mobility Engine",
                subtitle: "Debt-free rental fleet targeting rideshare + delivery gig workers.",
                brand: "10B981",
                accent: "22D3EE",
                slides: SLIDES.map((s) => ({ kicker: s.kicker, title: s.title, body: s.body, bullets: flattenSlideForPptx(s) })),
              })}
              data-testid="advantage-download-pptx"
              className="inline-flex items-center gap-2 border border-emerald-500/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
            >
              <Download size={12} /> Download PPT
            </button>
            <button onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }} data-testid="advantage-relock" className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all">
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
